import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Textarea } from '@/components/atoms/textarea';
import { useCloseLead } from '@/hooks/leads/useCloseLead';
import type { LeadLossReason } from '@/types/lead';

/**
 * A label per loss reason. `Record` over the union rather than a loose list, so
 * a reason added to `LeadLossReason` fails the typecheck here instead of quietly
 * going unofferable — the close endpoint would accept it and this dialog, the
 * only way a human records a loss, would never show it.
 */
const LOSS_REASON_LABELS: Record<LeadLossReason, string> = {
  PRICE: 'Price',
  TIMING: 'Timing',
  WENT_WITH_COMPETITOR: 'Went with competitor',
  NO_RESPONSE: 'No response',
  OUT_OF_SCOPE: 'Out of scope',
  // Kept distinct from "Out of scope" rather than folded into it: one says the
  // work is outside where we go, the other that it is outside what we do. Both
  // were offerable before the close flows split, and both map 1:1 from the
  // disqualification vocabulary now.
  OUT_OF_AREA: 'Out of area',
  WRONG_SERVICE: 'Wrong service',
  CANCELLED: 'Cancelled',
  OTHER: 'Other',
};

const LOSS_REASONS = Object.entries(LOSS_REASON_LABELS).map(([value, label]) => ({
  value: value as LeadLossReason,
  label,
}));

/** Radix hands back a bare string; this is what keeps state on the union. */
function isLeadLossReason(value: string): value is LeadLossReason {
  return value in LOSS_REASON_LABELS;
}

interface MarkLeadLostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  onSuccess?: () => void;
}

export function MarkLeadLostModal({
  open,
  onOpenChange,
  leadId,
  onSuccess,
}: MarkLeadLostModalProps) {
  const [lossReason, setLossReason] = useState<LeadLossReason | ''>('');
  const [lossReasonDetail, setLossReasonDetail] = useState('');
  const [competitorName, setCompetitorName] = useState('');
  const closeLead = useCloseLead();

  const canSubmit =
    !closeLead.isPending &&
    lossReason !== '' &&
    (lossReason !== 'WENT_WITH_COMPETITOR' || competitorName.trim() !== '');

  function resetForm() {
    setLossReason('');
    setLossReasonDetail('');
    setCompetitorName('');
  }

  function handleSubmit() {
    // The submit button is already gated on this; the check is what lets the
    // union narrow, so the payload below satisfies `CloseLeadDto`'s LOST member
    // rather than being asserted into it.
    if (lossReason === '') return;

    closeLead.mutate(
      {
        leadId,
        outcome: 'LOST',
        lossReason,
        ...(lossReasonDetail && { lossReasonDetail }),
        ...(lossReason === 'WENT_WITH_COMPETITOR' && { competitorName }),
      },
      {
        onSuccess: () => {
          resetForm();
          onOpenChange(false);
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetForm();
        onOpenChange(value);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark Lead Lost</DialogTitle>
          <DialogDescription>Capture why this opportunity did not convert.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="loss-reason">Loss reason</Label>
            <Select
              value={lossReason}
              onValueChange={(value) => {
                if (isLeadLossReason(value)) setLossReason(value);
              }}
            >
              <SelectTrigger id="loss-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {LOSS_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {lossReason === 'WENT_WITH_COMPETITOR' && (
            <div className="space-y-2">
              <Label htmlFor="competitor-name">Competitor name</Label>
              <Input
                id="competitor-name"
                value={competitorName}
                onChange={(event) => setCompetitorName(event.target.value)}
                placeholder="Enter competitor name"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="loss-detail">Details</Label>
            <Textarea
              id="loss-detail"
              value={lossReasonDetail}
              onChange={(event) => setLossReasonDetail(event.target.value)}
              placeholder="Add any useful context..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!canSubmit}>
            {closeLead.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Mark Lost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
